import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AlertCircle, TrendingDown, TrendingUp } from 'lucide-react';
import { getQualityAnalytics } from '@/data/conversationQuality';

interface QualityMetrics {
  avgQualityScore: number;
  autoBookingRate: number;
  bookingConversionRate: number;
  totalConversations: number;
  lowQualityCount: number;
}

export function ConversationQualityDashboard() {
  const [metrics, setMetrics] = useState<QualityMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      const data = await getQualityAnalytics();
      if (data) {
        setMetrics(data);
      }
      setLoading(false);
    };

    fetchMetrics();
  }, []);

  if (loading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-1/4" />
          <div className="h-20 bg-muted rounded" />
        </div>
      </Card>
    );
  }

  if (!metrics) {
    return (
      <Card className="p-6">
        <p className="text-muted-foreground">No conversation data available</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="text-lg font-semibold">Conversation Quality Insights</h3>
        <Badge variant="outline">Last 100 conversations</Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Average Quality Score */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Avg Quality Score</span>
            {metrics.avgQualityScore >= 70 ? (
              <TrendingUp className="h-4 w-4 text-green-500" />
            ) : (
              <TrendingDown className="h-4 w-4 text-yellow-500" />
            )}
          </div>
          <div className="text-3xl font-bold">{metrics.avgQualityScore}%</div>
          <p className="text-xs text-muted-foreground mt-1">
            {metrics.avgQualityScore >= 70 ? 'Good health' : 'Needs attention'}
          </p>
        </Card>

        {/* Auto-Booking Trigger Rate */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Auto-Booking Rate</span>
            <AlertCircle className="h-4 w-4 text-orange-500" />
          </div>
          <div className="text-3xl font-bold">{metrics.autoBookingRate}%</div>
          <p className="text-xs text-muted-foreground mt-1">
            Frustration detected
          </p>
        </Card>

        {/* Booking Conversion */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Booking Conversion</span>
          </div>
          <div className="text-3xl font-bold">{metrics.bookingConversionRate}%</div>
          <p className="text-xs text-muted-foreground mt-1">
            Of frustrated users
          </p>
        </Card>

        {/* Low Quality Count */}
        <Card className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Low Quality Chats</span>
          </div>
          <div className="text-3xl font-bold">{metrics.lowQualityCount}</div>
          <p className="text-xs text-muted-foreground mt-1">
            Score below 60%
          </p>
        </Card>
      </div>

      {/* Insights */}
      <Card className="p-4">
        <h4 className="font-semibold mb-2">Key Insights</h4>
        <ul className="space-y-2 text-sm">
          {metrics.avgQualityScore < 70 && (
            <li className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-yellow-500 mt-0.5" />
              <span>
                Average quality score is below 70%. Consider improving AI responses or
                suggesting human help earlier.
              </span>
            </li>
          )}
          {metrics.autoBookingRate > 30 && (
            <li className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5" />
              <span>
                High auto-booking rate ({metrics.autoBookingRate}%) indicates many users
                experiencing frustration. Review common frustration patterns.
              </span>
            </li>
          )}
          {metrics.bookingConversionRate < 30 && metrics.autoBookingRate > 0 && (
            <li className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
              <span>
                Low booking conversion ({metrics.bookingConversionRate}%). Users frustrated
                but not booking calls. Improve booking CTA messaging.
              </span>
            </li>
          )}
          {metrics.avgQualityScore >= 70 &&
            metrics.autoBookingRate < 20 &&
            metrics.bookingConversionRate > 50 && (
              <li className="flex items-start gap-2">
                <TrendingUp className="h-4 w-4 text-green-500 mt-0.5" />
                <span>
                  Excellent performance! Quality is high and frustrated users are
                  successfully converting to bookings.
                </span>
              </li>
            )}
        </ul>
      </Card>
    </div>
  );
}
